import { ForwardedRef, forwardRef, RefObject } from 'react'
import { IconButton, styled } from '@mui/material'
import { PauseIcon, PlayIcon } from '@weblite/icons'
import { ViewerTransformWrapper } from '$comp/modals/media-viewer/viewer-transform-wrapper'

const StyledVideo = styled('video')`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

interface PlayButtonProps {
  isPlaying: RefObject<boolean>
  onPause: () => void
  onPlay: () => void
}

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: 50,
  height: 50,
  position: 'absolute',
  left: 'calc(50% - 25px)',
  top: 'calc(50% - 25px)',
  color: theme.palette.secondary.main,
  borderRadius: '50%',
  zIndex: 5,

  boxShadow: '0px 1px 10px #0000001a',
  backgroundColor: '#505962cc',
  '&:hover': {
    backgroundColor: '#505962cc',
  },
}))

export const PlayButton = forwardRef(function PlayButton(
  { isPlaying, onPause, onPlay }: PlayButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <StyledIconButton ref={ref} onClick={isPlaying.current ? onPause : onPlay}>
      {isPlaying.current ? <PauseIcon /> : <PlayIcon />}
    </StyledIconButton>
  )
})

interface VideoPreviewProps {
  isPlaying: RefObject<boolean>
  videoRef: RefObject<HTMLVideoElement>
  videoContainerRef: RefObject<HTMLDivElement>
  playButtonRef: RefObject<HTMLButtonElement>
  src: string
  onPause: () => unknown
  onPlay: () => unknown
  onTimeUpdate: () => unknown
  onEnded: () => unknown
  poster?: string
  onPrev?: () => void
  onNext?: () => void
  onLoadedMetadata?: () => unknown
}

export function VideoPreview({
  src,
  poster = '',
  isPlaying,
  playButtonRef,
  videoRef,
  videoContainerRef,
  onPause,
  onPlay,
  onTimeUpdate,
  onEnded,
  onPrev,
  onNext,
  onLoadedMetadata,
}: VideoPreviewProps) {
  return (
    <Root ref={videoContainerRef}>
      <PlayButton
        ref={playButtonRef}
        isPlaying={isPlaying}
        onPause={onPause}
        onPlay={onPlay}
      />
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <ViewerTransformWrapper onPrev={onPrev} onNext={onNext}>
        <StyledVideo
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          preload="auto"
          autoPlay
          ref={videoRef}
          src={src}
          onLoadedMetadata={onLoadedMetadata}
          poster={poster}
        />
      </ViewerTransformWrapper>
    </Root>
  )
}

const Root = styled('div')`
  display: flex;
  flex-direction: column;
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: contain;
`
